import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  IndianRupee, TrendingUp, Clock, AlertTriangle, RefreshCw,
  CheckCircle2, ChevronDown, Plus, Pencil, Trash2, X, Check, Mail, Eye
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { can } from "@/lib/permissions";
import { todayIST } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = ["NEFT", "RTGS", "Cheque", "Cash", "UPI", "Other"];
const MODE_COLORS = {
  NEFT: "bg-blue-100 text-blue-700",
  RTGS: "bg-indigo-100 text-indigo-700",
  Cheque: "bg-purple-100 text-purple-700",
  Cash: "bg-green-100 text-green-700",
  UPI: "bg-orange-100 text-orange-700",
  Other: "bg-slate-100 text-slate-600",
};

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtDec = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const blankForm = () => ({
  project_id: "",
  date: todayIST(),
  amount: "",
  mode: "NEFT",
  reference: "",
  notes: "",
});

// ─── Add / Edit Payment Modal ────────────────────────────────────────────────
function PaymentModal({ projects, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || blankForm());
  const [saving, setSaving] = useState(false);
  const [pis, setPis] = useState([]);
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Invoices of the selected project, so the payment can be applied to a PI
  useEffect(() => {
    if (!form.project_id) { setPis([]); return; }
    axios.get(`${API}/financials/pi`, { params: { project_id: form.project_id } })
      .then(r => setPis((r.data || []).filter(p => p.status !== "void")))
      .catch(() => setPis([]));
  }, [form.project_id]);

  const handleSave = async () => {
    if (!form.project_id) { toast.error("Select a project"); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!form.date) { toast.error("Select a date"); return; }

    setSaving(true);
    try {
      const payload = { ...form, amount: amt };
      let saved;
      if (isEdit) {
        const { data } = await axios.put(`${API}/payments/${initial.id}`, payload);
        saved = data;
        toast.success("Payment entry updated");
      } else {
        const { data } = await axios.post(`${API}/payments`, payload);
        saved = data;
        toast.success(`Payment of ${fmtDec(amt)} recorded`);
      }
      onSaved(saved, isEdit);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save payment");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-fade-up flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0] shrink-0">
          <h3 className="font-heading font-bold text-base text-[#0A1128]">
            {isEdit ? "Edit Payment Entry" : "Record Payment Received"}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F8FAFC] text-[#64748B]"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Project */}
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Project *</label>
            <div className="relative">
              <select
                value={form.project_id}
                onChange={e => { set("project_id", e.target.value); set("pi_id", null); }}
                className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white appearance-none pr-8"
                data-testid="payment-project-select"
                disabled={isEdit}
              >
                <option value="">— Select Project —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
            </div>
          </div>

          {/* Apply to invoice (optional) */}
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Against Invoice (optional)</label>
            <div className="relative">
              <select
                value={form.pi_id || ""}
                onChange={e => set("pi_id", e.target.value || null)}
                className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white appearance-none pr-8"
                data-testid="payment-pi-select"
                disabled={!form.project_id}
              >
                <option value="">— Not linked to a PI —</option>
                {pis.map(pi => (
                  <option key={pi.id} value={pi.id}>
                    {pi.pi_number} · {fmt(pi.total)}{pi.paid_amount ? ` (paid ${fmt(pi.paid_amount)})` : ""}
                  </option>
                ))}
              </select>
              <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
            </div>
            {form.pi_id && <p className="text-[10px] text-[#8AA496] mt-1">The PI's paid amount and status are derived from linked payments.</p>}
          </div>

          {/* Date + Amount row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Date *</label>
              <input
                type="date"
                value={form.date}
                onChange={e => set("date", e.target.value)}
                className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                data-testid="payment-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Amount (₹) *</label>
              <input
                type="number"
                value={form.amount}
                onChange={e => set("amount", e.target.value)}
                placeholder="e.g. 350000"
                className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                min="1"
                step="0.01"
                data-testid="payment-amount-input"
              />
            </div>
          </div>

          {/* Mode + Reference row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Mode *</label>
              <div className="relative">
                <select
                  value={form.mode}
                  onChange={e => set("mode", e.target.value)}
                  className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white appearance-none pr-8"
                  data-testid="payment-mode-select"
                >
                  {MODES.map(m => <option key={m}>{m}</option>)}
                </select>
                <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">
                {form.mode === "Cheque" ? "Cheque No." : form.mode === "NEFT" || form.mode === "RTGS" ? "UTR No." : "Reference"}
              </label>
              <input
                type="text"
                value={form.reference}
                onChange={e => set("reference", e.target.value)}
                placeholder={form.mode === "Cheque" ? "CHQ123456" : form.mode === "UPI" ? "UPI Ref No." : "Optional"}
                className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                data-testid="payment-reference-input"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Notes / Remarks</label>
            <textarea
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder="e.g. Advance payment for Phase 2 mobilisation, per client email dated..."
              rows={2}
              className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none"
              data-testid="payment-notes-input"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 border border-[#E2E8F0] text-[#64748B] py-2.5 rounded text-sm font-medium hover:bg-[#F8FAFC] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-[#FFB703] text-[#0A1128] font-bold py-2.5 rounded text-sm hover:bg-[#E8A200] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="save-payment-btn"
          >
            {saving ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
            {saving ? "Saving..." : isEdit ? "Update Entry" : "Record Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Receipt Preview Modal ───────────────────────────────────────────────────
function ReceiptPreviewModal({ payment, onClose, onSend, onSaved }) {
  const [html, setHtml] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    date: payment.date || todayIST(),
    amount: payment.amount || "",
    mode: payment.mode || "NEFT",
    reference: payment.reference || "",
    notes: payment.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const loadPreview = async (paymentId) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/payments/${paymentId}/preview-receipt`);
      setHtml(data.html);
      setReceiptNumber(data.receipt_number);
      setClientEmail(data.client_email || "");
    } catch (err) {
      toast.error("Failed to load receipt preview");
      onClose();
    }
    setLoading(false);
  };

  useEffect(() => { loadPreview(payment.id); }, [payment.id]); // eslint-disable-line

  const setField = (k, v) => setEditForm(p => ({ ...p, [k]: v }));

  const handleSaveEdit = async () => {
    const amt = parseFloat(editForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!editForm.date) { toast.error("Select a date"); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/payments/${payment.id}`, {
        ...editForm,
        amount: amt,
      });
      toast.success("Payment updated");
      onSaved(data, true);
      setIsEditing(false);
      await loadPreview(payment.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update payment");
    }
    setSaving(false);
  };

  const handleSend = async () => {
    setSending(true);
    await onSend(payment.id);
    setSending(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl flex flex-col animate-fade-up" style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#E2E8F0] flex-shrink-0">
          <div>
            <h3 className="font-heading font-bold text-sm text-[#0A1128]">
              {isEditing ? "Edit Payment" : "Receipt Preview"}
            </h3>
            {!isEditing && receiptNumber && (
              <p className="text-xs text-[#64748B] mt-0.5">{receiptNumber}{clientEmail ? ` · Will be sent to ${clientEmail}` : " · No client email set"}</p>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F8FAFC] text-[#64748B]">
            <X size={16} />
          </button>
        </div>

        {/* Content area */}
        {isEditing ? (
          /* ── Inline Edit Form ── */
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Date *</label>
                <input
                  type="date"
                  value={editForm.date}
                  onChange={e => setField("date", e.target.value)}
                  className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                  data-testid="inline-edit-date"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Amount (₹) *</label>
                <input
                  type="number"
                  value={editForm.amount}
                  onChange={e => setField("amount", e.target.value)}
                  placeholder="e.g. 350000"
                  className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                  min="1"
                  step="0.01"
                  data-testid="inline-edit-amount"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Mode</label>
                <div className="relative">
                  <select
                    value={editForm.mode}
                    onChange={e => setField("mode", e.target.value)}
                    className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white appearance-none pr-8"
                    data-testid="inline-edit-mode"
                  >
                    {MODES.map(m => <option key={m}>{m}</option>)}
                  </select>
                  <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">
                  {editForm.mode === "Cheque" ? "Cheque No." : editForm.mode === "NEFT" || editForm.mode === "RTGS" ? "UTR No." : "Reference"}
                </label>
                <input
                  type="text"
                  value={editForm.reference}
                  onChange={e => setField("reference", e.target.value)}
                  placeholder="Optional"
                  className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                  data-testid="inline-edit-reference"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Notes / Remarks</label>
              <textarea
                value={editForm.notes}
                onChange={e => setField("notes", e.target.value)}
                placeholder="Optional notes..."
                rows={3}
                className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none"
                data-testid="inline-edit-notes"
              />
            </div>
          </div>
        ) : (
          /* ── Preview iframe ── */
          <div className="flex-1 overflow-hidden bg-[#F8FAFC] border-b border-[#E2E8F0]">
            {loading ? (
              <div className="flex items-center justify-center h-64 gap-2 text-[#64748B] text-sm">
                <RefreshCw size={14} className="animate-spin" /> Loading preview...
              </div>
            ) : (
              <iframe
                srcDoc={html}
                title="Receipt Preview"
                sandbox="allow-same-origin"
                className="w-full h-full"
                style={{ minHeight: "480px", border: "none" }}
              />
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 px-5 py-4 flex-shrink-0 border-t border-[#E2E8F0]">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-2 border border-[#E2E8F0] text-[#64748B] px-4 py-2 rounded text-sm font-medium hover:bg-[#F8FAFC] transition-colors"
                data-testid="inline-edit-cancel-btn"
              >
                Cancel
              </button>
              <div className="flex-1" />
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-bold px-4 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors disabled:opacity-50"
                data-testid="inline-edit-save-btn"
              >
                {saving ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
                {saving ? "Saving..." : "Save & Preview"}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 border border-[#E2E8F0] text-[#0A1128] px-4 py-2 rounded text-sm font-medium hover:bg-[#F1F5F9] transition-colors"
                data-testid="preview-edit-btn"
              >
                <Pencil size={13} /> Edit
              </button>
              <div className="flex-1" />
              <button
                onClick={onClose}
                className="border border-[#E2E8F0] text-[#64748B] px-4 py-2 rounded text-sm font-medium hover:bg-[#F8FAFC] transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !clientEmail}
                className="flex items-center gap-2 bg-[#2F7D52] text-white font-bold px-4 py-2 rounded text-sm hover:bg-[#266B44] transition-colors disabled:opacity-50"
                title={!clientEmail ? "No client email set on this project" : ""}
                data-testid="preview-send-btn"
              >
                {sending ? <RefreshCw size={13} className="animate-spin" /> : <Mail size={13} />}
                {sending ? "Sending..." : "Send Receipt"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Main Page ───────────────────────────────────────────────────────────────
export default function PaymentsPage({ embedded = false }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [projectFilter, setProjectFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [sendingReceiptId, setSendingReceiptId] = useState(null);
  const [previewPayment, setPreviewPayment] = useState(null);

  const canEdit = can(user, "client_payments.edit");
  const canSend = can(user, "receipts.send");

  const handleSendReceipt = async (paymentId) => {
    setSendingReceiptId(paymentId);
    try {
      const { data } = await axios.post(`${API}/payments/${paymentId}/send-receipt`);
      toast.success(data.message, { description: `Receipt No: ${data.receipt_number}` });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send receipt");
    }
    setSendingReceiptId(null);
  };

  const load = useCallback(async ({ silent = false } = {}) => {
    // silent: refresh totals in place — keep the table (and any open preview/edit modal) mounted
    if (!silent) setLoading(true);
    try {
      const [sumRes, projRes] = await Promise.all([
        axios.get(`${API}/payments/summary`),
        axios.get(`${API}/projects`),
      ]);
      setSummary(sumRes.data);
      setPayments(sumRes.data.payments || []);
      setProjects(projRes.data.filter(p => !p.is_archived));
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (entry, isEdit) => {
    if (isEdit) {
      setPayments(prev => prev.map(p => p.id === entry.id ? entry : p));
      setPreviewPayment(prev => (prev && prev.id === entry.id ? entry : prev));   // keep the open preview current
    } else {
      setPayments(prev => [entry, ...prev]);
    }
    // Refresh summary totals without unmounting the page
    load({ silent: true });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this payment entry? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/payments/${id}`);
      setPayments(prev => prev.filter(p => p.id !== id));
      toast.success("Payment entry deleted");
      load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
    setDeletingId(null);
  };

  const filteredPayments = payments.filter(p => {
    if (projectFilter !== "all" && p.project_id !== projectFilter) return false;
    if (modeFilter !== "all" && p.mode !== modeFilter) return false;
    return true;
  });

  const projNameMap = { ...(summary?.proj_map || {}), ...Object.fromEntries(projects.map(p => [p.id, p.name])) };

  if (loading) return (
    <div className="p-8 flex items-center gap-2 text-[#64748B] text-sm">
      <RefreshCw size={14} className="animate-spin" /> Loading...
    </div>
  );

  const { totals = {}, project_summaries = [] } = summary || {};

  return (
    <div className={embedded ? "p-6 max-w-6xl" : "p-6 max-w-6xl animate-fade-up"}>
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className={`font-heading font-bold text-[#0A1128] ${embedded ? "text-lg" : "text-2xl"}`}>Payments from Clients</h1>
          <p className="text-sm text-[#64748B] mt-0.5">{payments.length} payment entr{payments.length !== 1 ? "ies" : "y"} across {project_summaries.length} project{project_summaries.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={load} className="p-2 rounded border border-[#E2E8F0] hover:bg-[#F1F5F9] text-[#64748B]" title="Refresh">
            <RefreshCw size={13} />
          </button>
          {canEdit && (
            <button
              onClick={() => { setEditEntry(null); setShowModal(true); }}
              className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-bold px-4 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors"
              data-testid="add-payment-btn"
            >
              <Plus size={14} /> Record Payment
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Contract Value", value: totals.contract_value, icon: IndianRupee, cls: "text-[#0A1128] bg-[#F8FAFC]" },
          { label: "Total Billed (PIs)", value: totals.total_billed, icon: TrendingUp, cls: "text-blue-700 bg-blue-50" },
          { label: "Total Received", value: totals.total_received, icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50" },
          { label: "Balance Pending", value: totals.balance, icon: totals.balance > 0 ? AlertTriangle : CheckCircle2, cls: totals.balance > 0 ? "text-red-700 bg-red-50" : "text-emerald-700 bg-emerald-50" },
        ].map(card => (
          <div key={card.label} className={`border border-[#E2E8F0] rounded-lg p-4 ${card.cls.split(" ")[1]}`} data-testid={`card-${card.label.replace(/[^a-z0-9]+/gi,"-").toLowerCase()}`}>
            <div className={`flex items-center gap-1.5 mb-1 ${card.cls.split(" ")[0]}`}>
              <card.icon size={13} />
              <span className="text-[10px] font-bold uppercase tracking-wider">{card.label}</span>
            </div>
            <div className={`font-heading font-black text-xl ${card.cls.split(" ")[0]}`}>{fmt(card.value)}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F1F5F9] p-1 rounded-lg w-fit mb-5">
        {[["overview", "Project Overview"], ["ledger", "Payment Ledger"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded text-xs font-semibold transition-all ${tab === k ? "bg-white text-[#0A1128] shadow-sm" : "text-[#64748B] hover:text-[#0A1128]"}`}
            data-testid={`tab-${k}`}>
            {l}
          </button>
        ))}
      </div>

      {/* ── Overview Tab ── */}
      {tab === "overview" && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-[#F5F8F5] text-[#1B2B22] text-xs">
                <th className="px-4 py-3 text-left font-semibold">Project</th>
                <th className="px-4 py-3 text-right font-semibold">Contract Value</th>
                <th className="px-4 py-3 text-right font-semibold">Total Billed</th>
                <th className="px-4 py-3 text-right font-semibold">Received</th>
                <th className="px-4 py-3 text-right font-semibold">Balance</th>
                <th className="px-4 py-3 text-left font-semibold min-w-32">Recovery</th>
              </tr>
            </thead>
            <tbody>
              {project_summaries.map((s, i) => (
                <tr key={s.project_id} className={`border-b border-[#F1F5F9] ${i % 2 ? "bg-[#F8FAFC]" : ""}`} data-testid={`overview-row-${s.project_id}`}>
                  <td className="px-4 py-3 font-medium text-[#0A1128]">{s.project_name}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#64748B]">{fmt(s.contract_value)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-blue-700 font-medium">{fmt(s.total_billed)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-semibold">{fmt(s.total_received)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums font-bold ${s.balance > 0 ? "text-red-600" : "text-emerald-600"}`}>
                    {s.balance > 0 ? fmt(s.balance) : "Nil"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-[#E2E8F0] rounded-full overflow-hidden min-w-20">
                        <div className="h-2 rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(s.received_pct, 100)}%` }} />
                      </div>
                      <span className="text-xs text-[#64748B] w-9 text-right">{s.received_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {project_summaries.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[#94A3B8]">No projects found. Add payment entries to see summary.</td></tr>
              )}
            </tbody>
            {project_summaries.length > 0 && (
              <tfoot>
                <tr className="bg-[#FEF9E7] border-t-2 border-[#FFB703] font-bold text-sm">
                  <td className="px-4 py-3 text-[#0A1128]">Total</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmt(totals.contract_value)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-blue-700">{fmt(totals.total_billed)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{fmt(totals.total_received)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600">{fmt(totals.balance)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* ── Ledger Tab ── */}
      {tab === "ledger" && (
        <div>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#64748B] font-medium whitespace-nowrap">Project:</label>
              <div className="relative">
                <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
                  className="border border-[#E2E8F0] rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#FFB703] appearance-none pr-7"
                  data-testid="filter-project">
                  <option value="all">All Projects</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#64748B] font-medium">Mode:</label>
              <div className="relative">
                <select value={modeFilter} onChange={e => setModeFilter(e.target.value)}
                  className="border border-[#E2E8F0] rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#FFB703] appearance-none pr-7"
                  data-testid="filter-mode">
                  <option value="all">All Modes</option>
                  {MODES.map(m => <option key={m}>{m}</option>)}
                </select>
                <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
              </div>
            </div>
            <span className="text-xs text-[#94A3B8]">{filteredPayments.length} entr{filteredPayments.length !== 1 ? "ies" : "y"}</span>
            {filteredPayments.length > 0 && (
              <span className="text-xs font-semibold text-emerald-700 ml-auto">
                Total: {fmtDec(filteredPayments.reduce((a, p) => a + p.amount, 0))}
              </span>
            )}
          </div>

          {/* Ledger table */}
          {filteredPayments.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
              <IndianRupee size={32} className="text-[#E2E8F0] mx-auto mb-3" />
              <p className="text-sm text-[#64748B] font-medium">No payment entries yet</p>
              <p className="text-xs text-[#94A3B8] mt-1">Click "Record Payment" to add the first entry</p>
            </div>
          ) : (
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="bg-[#F5F8F5] text-[#1B2B22] text-xs">
                    <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">Date</th>
                    <th className="px-4 py-3 text-left font-semibold">Project</th>
                    <th className="px-4 py-3 text-left font-semibold">Invoice</th>
                    <th className="px-4 py-3 text-right font-semibold">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold">Mode</th>
                    <th className="px-4 py-3 text-left font-semibold">Reference</th>
                    <th className="px-4 py-3 text-left font-semibold">Notes</th>
                    {(canEdit || canSend) && <th className="px-4 py-3 text-center font-semibold w-28">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((p, i) => (
                    <tr key={p.id} className={`border-b border-[#F1F5F9] ${i % 2 ? "bg-[#F8FAFC]" : ""}`} data-testid={`ledger-row-${p.id}`}>
                      <td className="px-4 py-3 text-xs text-[#64748B] whitespace-nowrap font-medium">
                        {fmtDate(p.date)}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#0A1128] font-medium">
                        {projNameMap[p.project_id] || p.project_id}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#64748B] font-mono whitespace-nowrap">
                        {p.pi_number || <span className="text-[#CBD5E1]">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold text-emerald-700 whitespace-nowrap">
                        {fmtDec(p.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${MODE_COLORS[p.mode] || MODE_COLORS.Other}`}>
                          {p.mode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#64748B] font-mono">
                        {p.reference || <span className="text-[#CBD5E1]">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#64748B] max-w-48 truncate" title={p.notes}>
                        {p.notes || <span className="text-[#CBD5E1]">—</span>}
                      </td>
                      {(canEdit || canSend) && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setPreviewPayment(p)}
                              className="p-1.5 rounded hover:bg-blue-50 text-[#94A3B8] hover:text-blue-600 transition-colors"
                              title="Preview receipt"
                              data-testid={`preview-receipt-${p.id}`}
                            >
                              <Eye size={12} />
                            </button>
                            <button
                              onClick={() => handleSendReceipt(p.id)}
                              disabled={sendingReceiptId === p.id}
                              className="p-1.5 rounded hover:bg-emerald-50 text-[#94A3B8] hover:text-emerald-600 transition-colors"
                              title="Email receipt to client"
                              data-testid={`send-receipt-${p.id}`}
                            >
                              {sendingReceiptId === p.id
                                ? <RefreshCw size={12} className="animate-spin" />
                                : <Mail size={12} />}
                            </button>
                            <button
                              onClick={() => { setEditEntry(p); setShowModal(true); }}
                              className="p-1.5 rounded hover:bg-[#F1F5F9] text-[#94A3B8] hover:text-[#0A1128] transition-colors"
                              title="Edit"
                              data-testid={`edit-payment-${p.id}`}
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              onClick={() => handleDelete(p.id)}
                              disabled={deletingId === p.id}
                              className="p-1.5 rounded hover:bg-red-50 text-[#94A3B8] hover:text-red-600 transition-colors"
                              title="Delete"
                              data-testid={`delete-payment-${p.id}`}
                            >
                              {deletingId === p.id ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#F8FAFC] border-t border-[#E2E8F0]">
                    <td colSpan={3} className="px-4 py-2.5 text-xs font-semibold text-[#64748B]">
                      {filteredPayments.length} entries
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-bold text-emerald-700 text-sm">
                      {fmtDec(filteredPayments.reduce((a, p) => a + p.amount, 0))}
                    </td>
                    <td colSpan={(canEdit || canSend) ? 4 : 3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <PaymentModal
          projects={projects}
          initial={editEntry}
          onClose={() => { setShowModal(false); setEditEntry(null); }}
          onSaved={handleSaved}
        />
      )}

      {/* Receipt Preview Modal */}
      {previewPayment && (
        <ReceiptPreviewModal
          payment={previewPayment}
          onClose={() => setPreviewPayment(null)}
          onSaved={handleSaved}
          onSend={handleSendReceipt}
        />
      )}
    </div>
  );
}
